import React, { useMemo } from 'react';
import { Icons } from '../Icons';

/**
 * Checkbox — Styled checkbox with optional label and indeterminate state.
 *
 * The native <input type="checkbox"> is visually hidden but remains in the DOM
 * for accessibility (screen readers, form submission). The visual checkbox is
 * rendered via the .sc-box div with icon overlays.
 *
 * Indeterminate state: Used when a "select all" checkbox represents partial
 * selection (some items checked, some not). Set via the ref callback since
 * there's no HTML attribute for it.
 *
 * @param {boolean} checked       - Whether the checkbox is checked
 * @param {boolean} indeterminate - Whether to show indeterminate (dash) state
 * @param {boolean} disabled      - Disabled state
 * @param {func}    onChange       - Change handler
 * @param {string}  label         - Optional label text
 */
export const Checkbox = ({
    checked = false,
    indeterminate = false,
    disabled = false,
    onChange,
    label,
    className = "",
    ...props
}) => {
    const computedClass = useMemo(() =>
        [
            "sc-checkbox",
            checked && !indeterminate ? "sc-checked" : "",
            indeterminate ? "sc-indeterminate" : "",
            disabled ? "sc-disabled" : "",
            className
        ].filter(Boolean).join(" "),
        [checked, indeterminate, disabled, className]
    );

    return (
        <label className={computedClass}>
            <input 
                type="checkbox" 
                checked={checked} 
                disabled={disabled}
                onChange={onChange}
                ref={input => {
                    // indeterminate can only be set via JS — no HTML attribute exists
                    if (input) {
                        input.indeterminate = indeterminate;
                    }
                }}
                {...props}
            />
            <div className="sc-control">
                <div className="sc-box">
                    {/* Show dash icon for indeterminate, checkmark when checked */}
                    {indeterminate ? (
                        <Icons name="minus" size={16} />
                    ) : checked ? (
                        <Icons name="check" size={16} />
                    ) : null}
                </div>
            </div>
            {label && <span className="sc-label body-m-semibold">{label}</span>}
        </label>
    );
};
